import {
  FormLabel,
  NumberInputField,
  NumberInput,
  Select,
  useToast,
} from "@chakra-ui/react";
import { TransactionType, OrderTypes } from "../views/Orders/enums";
import { useState, useEffect } from "react";
import { isInvalidDiscountAndCredit } from "../utils/util";
import GaraazAlertDialogue from "./GaraazAlertDialogue";
import services from "../views/Orders/services";

interface TransactionTypeSelectComponentProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (
    transactionType: string,
    discount?: number,
    creditNotePercentage?: number,
  ) => void | Promise<void>;
  onFail?: () => void;
  supplierName?: string;
  supplierId?: string;
  garaazSupplierIdFromConfig?: string;
  orderId: string;
  setTransactionType: (transactionType: string) => void;
  orderData?: any;
  isLoading?: boolean;
}

const TransactionTypeSelectComponent = ({
  isOpen,
  onClose,
  onSuccess,
  onFail,
  supplierName,
  supplierId,
  garaazSupplierIdFromConfig,
  orderId,
  setTransactionType,
  orderData,
  isLoading = false,
}: TransactionTypeSelectComponentProps) => {
  const [selectedTransactionType, setSelectedTransactionType] =
    useState<string>("");
  const [discount, setDiscount] = useState<string>("");
  const [creditNotePercentage, setCreditNotePercentage] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const toast = useToast();

  const isRegularOrVirtualOrder =
    orderData?.orderType === OrderTypes.REGULAR ||
    orderData?.orderType === OrderTypes.VIRTUAL;

  const isSupplierGaraaz = Boolean(
    isRegularOrVirtualOrder &&
    supplierId &&
    supplierId === (garaazSupplierIdFromConfig || "64ae87c051c0be36170dc718"),
  );

  useEffect(() => {
    if (isOpen) {
      let transactionType = "";
      if (isSupplierGaraaz) {
        transactionType = TransactionType.TRADING;
      } else if (
        orderData &&
        orderData.orderItems &&
        orderData.orderItems.length > 0
      ) {
        const brands = new Set<string>();
        let foundInvalidBrand = false;
        for (const part of orderData.orderItems) {
          if (part && part.brand) {
            const normalizedBrand = part.brand.trim().toLowerCase();
            brands.add(normalizedBrand);
          } else {
            foundInvalidBrand = true;
            break;
          }
        }
        if (
          !foundInvalidBrand &&
          brands.size === 1 &&
          brands.has("maruti suzuki")
        ) {
          transactionType = TransactionType.FACILITATION;
        }
      }
      setSelectedTransactionType(transactionType);
      setDiscount("");
      setCreditNotePercentage("");
    }
  }, [isOpen, isSupplierGaraaz]);

  const handleTransactionTypeChange = (value: string) => {
    if (isSupplierGaraaz && value === TransactionType.FACILITATION) {
      toast({
        title: "Transaction Type Restricted",
        description: "Facilitation is not possible when allocating to Garaaz.",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    setSelectedTransactionType(value);
  };

  const handleDiscountAndCreditNotePercentageChange = (
    value: string,
    field: string,
  ) => {
    const numValue = Number(value);
    if (numValue >= 0 && numValue <= 100) {
      if (field === "Discount") {
        setDiscount(value);
      } else if (field === "Customer credit") {
        setCreditNotePercentage(value);
      }
    }
  };

  const handleConfirm = async () => {
    if (!selectedTransactionType) {
      return;
    }

    if (selectedTransactionType === TransactionType.TRADING) {
      const isInvalid = isInvalidDiscountAndCredit(
        +discount,
        +creditNotePercentage,
      );
      if (isInvalid) {
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Update transaction type in the order
      if (orderId && setTransactionType) {
        const payload: {
          orderDiscount?: string;
          creditNotePercentage?: string;
          transactionType: string;
        } = {
          transactionType: selectedTransactionType,
        };

        if (selectedTransactionType === "Trading") {
          payload.orderDiscount = discount;
          payload.creditNotePercentage = creditNotePercentage;
        }

        await services.updateOrder(orderId, payload);
        setTransactionType(selectedTransactionType);
      }

      await onSuccess(
        selectedTransactionType,
        selectedTransactionType === TransactionType.TRADING
          ? +discount
          : undefined,
        selectedTransactionType === TransactionType.TRADING
          ? +creditNotePercentage
          : undefined,
      );
    } catch (error) {
      console.error("Error updating transaction type:", error);
      onFail?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
    onFail?.();
  };

  const isSubmitDisabled =
    !selectedTransactionType ||
    (selectedTransactionType === TransactionType.TRADING &&
      isInvalidDiscountAndCredit(+discount, +creditNotePercentage));

  return (
    <GaraazAlertDialogue
      isOpen={isOpen}
      onOpen={() => {}}
      onClose={onClose}
      onSubmitBtn={handleConfirm}
      alertBodyText={
        <>
          <FormLabel
            marginRight={"auto"}
            fontSize={12}
            fontWeight={600}
            marginBottom={0}
          >
            Transaction Type
          </FormLabel>
          <Select
            placeholder="Transaction Type"
            value={selectedTransactionType}
            onChange={(e) => handleTransactionTypeChange(e.target.value)}
          >
            <option
              value={`${TransactionType.FACILITATION}`}
              disabled={isSupplierGaraaz}
            >
              {TransactionType.FACILITATION}
            </option>
            <option value={`${TransactionType.TRADING}`}>
              {TransactionType.TRADING}
            </option>
          </Select>
          {selectedTransactionType === TransactionType.TRADING && (
            <>
              <FormLabel
                marginRight={"auto"}
                fontSize={12}
                fontWeight={600}
                marginTop={4}
                marginBottom={0}
              >
                Discount
              </FormLabel>

              <NumberInput
                width={"100%"}
                value={discount}
                onChange={(value) => {
                  handleDiscountAndCreditNotePercentageChange(
                    value,
                    "Discount",
                  );
                }}
                size={"sm"}
              >
                <NumberInputField placeholder="Enter Customer Discount %" />
              </NumberInput>
              <FormLabel
                marginRight={"auto"}
                fontSize={12}
                fontWeight={600}
                marginTop={4}
                marginBottom={0}
              >
                Credit
              </FormLabel>

              <NumberInput
                width={"100%"}
                value={creditNotePercentage}
                onChange={(value) => {
                  handleDiscountAndCreditNotePercentageChange(
                    value,
                    "Customer credit",
                  );
                }}
                size={"sm"}
              >
                <NumberInputField placeholder="Enter Customer Credit %" />
              </NumberInput>
            </>
          )}
        </>
      }
      alertDialogueHeaderText={`Select Transaction Type for ${
        supplierName || "Supplier"
      }`}
      leftBtnText="Cancel"
      submitBtnText="Confirm"
      isSubmitBtnDisabled={isSubmitDisabled}
      isSubmitBtnLoading={isSubmitting || isLoading}
      isCentered={true}
    />
  );
};

export default TransactionTypeSelectComponent;
